import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Calendar, Download, Loader2, RefreshCw } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip
} from 'recharts';
import { SEMAFORO_COLORS } from '../constants/semaforo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Labels y colores del semáforo: SIEMPRE tomados de la fuente única (constants/semaforo.js),
// nunca redefinidos a mano acá para evitar drift de nombres/colores entre vistas.
const COLOR_ORDER = ['green', 'blue', 'purple', 'orange', 'yellow', 'red', 'dark_gray', 'black'];
const COLOR_META = Object.fromEntries(
  COLOR_ORDER.map(c => [c, { hex: SEMAFORO_COLORS[c].hex, label: SEMAFORO_COLORS[c].label }])
);

const sinPeriodoLabel = (data) => (data?.is_ccf ? 'Etapa 1 (histórico)' : 'Sin período');

const downloadCsv = (data, periodosVisibles, scopeLabel) => {
  const header = ['Periodo', 'Total', ...COLOR_ORDER.map(c => COLOR_META[c].label)];
  const rows = periodosVisibles.map(p => [
    p.periodo_display, p.total, ...COLOR_ORDER.map(c => p.semaforo[c] || 0)
  ]);
  if (data.sin_periodo.total > 0) {
    rows.push([sinPeriodoLabel(data), data.sin_periodo.total, ...COLOR_ORDER.map(c => data.sin_periodo.semaforo[c] || 0)]);
  }
  const csv = [header, ...rows].map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `academia_por_periodo_${data.partner_name.replace(/\s+/g, '_')}_${scopeLabel}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const PctTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs space-y-1 min-w-[180px]">
      <p className="font-semibold text-slate-800 mb-1">{label} · {row.total} alumnos</p>
      {COLOR_ORDER.filter(c => row[`${c}_raw`] > 0).map(c => (
        <p key={c} className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1.5 text-slate-600">
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: COLOR_META[c].hex }} />
            {COLOR_META[c].label}
          </span>
          <span className="font-medium tabular-nums" style={{ color: COLOR_META[c].hex }}>
            {row[`${c}_raw`]} ({row[c].toFixed(0)}%)
          </span>
        </p>
      ))}
    </div>
  );
};

export const AcademiaPorPeriodo = ({ partnerId, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('12m'); // '12m' | 'all'

  const loadData = useCallback(async () => {
    if (!partnerId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/partners/${partnerId}/academia-por-periodo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('No se pudo cargar Academia por Período');
      const json = await response.json();
      setData(json);
    } catch (e) {
      console.error('Error cargando academia-por-periodo:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [partnerId, token]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { setViewMode('12m'); }, [partnerId]);

  const hasSinPeriodo = data?.sin_periodo?.total > 0;
  const hasAnyData = (data?.periodos?.length > 0) || hasSinPeriodo;

  // Los períodos ya llegan ordenados cronológicamente (asc) desde el backend.
  const periodosVisibles = useMemo(() => {
    if (!data) return [];
    return viewMode === 'all' ? data.periodos : data.periodos.slice(-12);
  }, [data, viewMode]);

  const pctChartData = useMemo(() => periodosVisibles.map(p => {
    const row = { periodo: p.periodo_display, total: p.total };
    COLOR_ORDER.forEach(c => {
      const count = p.semaforo[c] || 0;
      row[c] = p.total > 0 ? (count / p.total) * 100 : 0;
      row[`${c}_raw`] = count;
    });
    return row;
  }), [periodosVisibles]);

  if (!partnerId) return null;

  const truncado = data && viewMode === '12m' && data.periodos.length > 12;

  return (
    <Card className="bg-white border border-slate-200 rounded-xl" data-testid="academia-por-periodo-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-indigo-600" />
            Academia por Período (mes de inscripción)
          </h3>
          <div className="flex items-center gap-2">
            {hasAnyData && (
              <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" data-testid="academia-periodo-toggle">
                <button
                  type="button"
                  onClick={() => setViewMode('12m')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === '12m' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="academia-periodo-toggle-12m"
                >
                  12 meses
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('all')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'all' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="academia-periodo-toggle-all"
                >
                  Todo el histórico
                </button>
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={loadData} disabled={loading} data-testid="academia-periodo-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => downloadCsv(data, periodosVisibles, viewMode === 'all' ? 'todo' : 'ultimos-12-meses')}
              disabled={!data || !hasAnyData}
              data-testid="academia-periodo-export-csv-btn"
            >
              <Download className="w-4 h-4 mr-1.5" /> Exportar CSV
            </Button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-10 text-slate-400" data-testid="academia-periodo-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando períodos...
          </div>
        )}
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="academia-periodo-error">
            {error}
          </div>
        )}

        {!loading && !error && data && !hasAnyData && (
          <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg" data-testid="academia-periodo-empty">
            No se pudo determinar el mes de inscripción de ningún alumno todavía.
          </div>
        )}

        {!loading && !error && data && hasAnyData && (
          <>
            {/* Único gráfico: barra apilada al 100% - composición del semáforo por mes */}
            {pctChartData.length > 0 && (
              <div style={{ width: '100%', height: 220 }} data-testid="academia-periodo-pct-chart">
                <ResponsiveContainer>
                  <BarChart data={pctChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="periodo" tick={{ fontSize: 10 }} interval={pctChartData.length > 14 ? 'preserveStartEnd' : 0} />
                    <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={38} />
                    <RechartsTooltip content={<PctTooltip />} />
                    {COLOR_ORDER.map(c => (
                      <Bar key={c} dataKey={c} stackId="pct" fill={COLOR_META[c].hex} name={COLOR_META[c].label} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Tabla protagonista: 1 fila por mes, header fijo, números alineados */}
            <div className="border rounded-lg overflow-hidden" data-testid="academia-periodo-table">
              <div className="max-h-[460px] overflow-y-auto overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-indigo-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 text-left whitespace-nowrap">Mes</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap">Total</th>
                      {COLOR_ORDER.map(c => (
                        <th key={c} className="px-2 py-2 text-right whitespace-nowrap" style={{ color: COLOR_META[c].hex }}>
                          {COLOR_META[c].label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {periodosVisibles.map(p => (
                      <tr key={p.periodo} className="hover:bg-indigo-50/50" data-testid={`academia-periodo-row-${p.periodo}`}>
                        <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{p.periodo_display}</td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums">{p.total}</td>
                        {COLOR_ORDER.map(c => (
                          <td key={c} className="px-2 py-2 text-right text-slate-600 tabular-nums">{p.semaforo[c] || 0}</td>
                        ))}
                      </tr>
                    ))}
                    {hasSinPeriodo && (
                      <tr className="bg-slate-50" data-testid="academia-periodo-row-sin-periodo">
                        <td className="px-3 py-2 font-medium text-slate-500 whitespace-nowrap">{sinPeriodoLabel(data)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-500 tabular-nums">{data.sin_periodo.total}</td>
                        {COLOR_ORDER.map(c => (
                          <td key={c} className="px-2 py-2 text-right text-slate-400 tabular-nums">{data.sin_periodo.semaforo[c] || 0}</td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {truncado && (
              <p className="text-xs text-slate-400" data-testid="academia-periodo-truncated-note">
                Mostrando los últimos 12 meses ({data.periodos.length} en total). Usá "Todo el histórico" para verlos todos.
              </p>
            )}
            {data.is_ccf && hasSinPeriodo && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="academia-periodo-ccf-warning">
                CCF: {data.sin_periodo.total} alumnos sin mes de inscripción determinable (su nombre de sección no tiene el token de fecha AAAAMMDD).
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AcademiaPorPeriodo;
